import { useEffect, useState } from 'react';
import { TransactionSection } from './TransactionSection';
import './Style.css';
import { TbTransfer, TbReceipt2, TbFileText } from "react-icons/tb";
import { FaBarcode, FaDollarSign, FaHashtag, FaPix, FaRegFlag } from "react-icons/fa6";
import { BsQrCode } from 'react-icons/bs';
import { getCNPJ } from '../../../services/getCNPJ';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { MdDownload, MdOutlineArrowBack, MdOutlineDateRange } from 'react-icons/md';
import { BiChevronDown, BiChevronUp } from 'react-icons/bi';
import { apiV1 } from '../../../lib/axios';

const formatValueToMonetary = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const tedTypes = ['PreDischargeAccount', 'CreditTedIn', 'RefundTED', 'CancelDebitDischarge'];
const pixTypes = ['CreditPixIn', 'PreDebitPixOut', 'CreditRefundPixOut'];
const boletoTypes = ['CreditBoleto', 'PreDebitBoletoOut', 'CancelBoletoOut'];

const transactionStatuses = {
  'Canceled': 'Cancelado',
  'Paid': 'Pago',
  'Created': 'Processando',
  'Settled': 'Pago',
}

export default function TransactionDetails(props) {
  const [loader, setLoader] = useState(false);
  const [mapUrl, setMapUrl] = useState(null);

  const [isPaymentExpanded, setIsPaymentExpanded] = useState(true);
  const [isLocationExpanded, setIsLocationExpanded] = useState(true);

  const data = props.data;

  function getStatusColor(status) {
    if(!status) return '';

    status = status.toUpperCase();
    const statusmap = {
      'SETTLED': 'status-paid',
      'PAID': 'status-paid',
      'CANCELED': 'status-canceled',
      'CREATED': 'status-processing',
    }

    return statusmap[status] || '';
  }

  const fetchVoucher = async () => {
    setLoader(true);
    try {
      const response = await apiV1.post('/voucher/fetch', {
        type: data.type,
        documentNumber: data.documentNumber,
      });
      const url = response.data.url;
      window.open(url, '_blank');
    } catch (error) {
      console.error('Erro ao criar o comprovante:', error);
      throw error;
    } finally {
      setLoader(false);
    }
  };

  const getTransactionIcon = (type, isQrCode) => {
    if (pixTypes.includes(type) && isQrCode) return <BsQrCode size={20}/>;
    if (pixTypes.includes(type)) return <FaPix size={20}/>;
    if (tedTypes.includes(type)) return <TbTransfer size={20}/>;
    if (boletoTypes.includes(type)) return <TbReceipt2 size={20}/>;
    return <TbReceipt2 />;
  };

  const getTransactionTitle = (type, isQrCode) => {
    if (pixTypes.includes(type) && isQrCode) return "Dados do Pagamento PIX";
    if (pixTypes.includes(type)) return "Dados da Transferência PIX";
    if (tedTypes.includes(type)) return "Dados da Transferência TED";
    if (boletoTypes.includes(type)) return "Dados do Pagamento de Boleto";
    return "Dados da Transação";
  }

  const getTransactionColor = (value) => {
    return value < 0 ? 'transaction-negative' : 'transaction-positive';
  };

  const getMapUrl = async () => {
    if (!data.receiverTaxNumber || data.receiverTaxNumber.length < 14) return;

    const cnpjInfos = await getCNPJ(data.receiverTaxNumber);
    const address = `${cnpjInfos.address.street}, ${cnpjInfos.address.number} - ${cnpjInfos.address.neighborhood}, ${cnpjInfos.address.city} - ${cnpjInfos.address.state}`;
    const mapUrl = `https://maps.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;
    setMapUrl(mapUrl);
  }

  useEffect(() => {
    getMapUrl();
  }, [data.receiverTaxNumber]);

  return (
    <div className="modern-transaction-details">
      <div className="transaction-details-header">
        <h1>Detalhamento da Transação</h1>
        <p>Informações completas da sua movimentação financeira</p>
      </div>

      <div className="main-content">
        <div className="transaction-flow">
          <TransactionSection 
            title="Origem" 
            data={{
              name: data.payerName,
              taxNumber: data.payerTaxNumber,
              bank: data.payerBank,
              branch: data.payerBankBranch,
              account: data.payerBankAccount,
              digit: data.payerBankAccountDigit
            }} 
          />

          <div className="flow-arrow">
            <i className="icon icon-arrow-right"></i>
          </div>

          <TransactionSection 
            title="Destino" 
            data={{
              name: data.receiverName,
              taxNumber: data.receiverTaxNumber,
              bank: data.receiverBank,
              branch: data.receiverBankBranch,
              account: data.receiverBankAccount,
              digit: data.receiverBankAccountDigit
            }} 
            isDestination={true}
          />
        </div>

        <div className="payment-details expandable-section">
          <div 
            className="payment-header expandable-header"
            onClick={() => setIsPaymentExpanded(!isPaymentExpanded)}
          >
            <div className="payment-icon">
              {getTransactionIcon(data.type, data.isQrCode)}
            </div>
            <h2>{getTransactionTitle(data.type, data.isQrCode)}</h2>
            <div className="expand-icon">
              {isPaymentExpanded ? <BiChevronUp size={16} /> : <BiChevronDown size={16} />}
            </div>
          </div>

          <div className={`expandable-content ${isPaymentExpanded ? 'expanded' : 'collapsed'}`}>
            <div className="row g-2">
              <div className="col-lg-6">
                <div className="payment-item">
                  <label>
                    <FaHashtag size={15} />
                    Identificador
                  </label>
                  <p>{data.documentNumber}</p>
                </div>
              </div>

              {data.barCode && (
                <div className="col-lg-6">
                  <div className="payment-item">
                    <label>
                      <FaBarcode size={15} />
                      Código de Barras
                    </label>
                    <p>{data.barCode}</p>
                  </div>
                </div>
              )}

              <div className="col-lg-6">
                <div className={`payment-item value-highlight ${getTransactionColor(data.value)}`}>
                  <label>
                    <FaDollarSign size={15} />
                    Valor da Transação
                  </label>
                  <p>{formatValueToMonetary(Math.abs(Number(data.value)))}</p>
                </div>
              </div>

              <div className="col-lg-6">
                <div className={`payment-item`}>
                  <label>
                    <MdOutlineDateRange size={15} />
                    Data/Hora
                  </label>
                  <p>{new Date(data?.date || data?.entryDate).toLocaleString('pt-BR')}</p>
                </div>
              </div>
              {
                data.status && 
                  <div className="col-lg-6">
                    <div className={`payment-item  status-highlight ${getStatusColor(data.status)}`}>
                      <label>
                        <FaRegFlag size={15} />
                        Status
                      </label>
                      <p>{transactionStatuses[data.status]}</p>
                    </div>
                  </div>
              }
              {
                data.customDescription && 
                  <div className="col-lg-6">
                    <div className={`payment-item`}>
                      <label>
                        <TbFileText size={15} />
                        Descrição
                      </label>
                      <p>{data.customDescription}</p>
                    </div>
                  </div>
              }
            </div>
          </div>
        </div>

        {
          (data.receiverTaxNumber && data.receiverTaxNumber.length >= 14) && (
            <div class="payment-details expandable-section">
              <div 
                class="payment-header expandable-header"
                onClick={() => setIsLocationExpanded(!isLocationExpanded)}
              >
                <div className="payment-icon">
                  <FaMapMarkerAlt size={20} />
                </div>
                <h2>Localização da Transação</h2>
                <div className="expand-icon">
                  {isLocationExpanded ? <BiChevronUp size={16} /> : <BiChevronDown size={16} />}
                </div>
              </div>
              <div className={`expandable-content ${isLocationExpanded ? 'expanded' : 'collapsed'}`}>
                <div class="card-body p-0">
                  <iframe
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    loading="lazy"
                    allowFullScreen
                    src={mapUrl}
                  ></iframe>
                </div>
              </div>
            </div>
          )
        }

        <div className="actions-container">
          <button 
            className="btn-custom btn-secondary-custom" 
            onClick={() => props.closeDetails && props.closeDetails()}
          >
            <MdOutlineArrowBack size={20} />
            Voltar
          </button>

          <button 
            className="btn-custom btn-primary-custom" 
            onClick={fetchVoucher}
            disabled={loader}
          >
            {loader ? (
              <>
                <div className="loading-spinner"></div>
                Gerando PDF...
              </>
            ) : (
              <>
                <MdDownload size={20} />
                Baixar Comprovante
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}